import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Text, Theme } from '@simplybenefits/sb-ui-library';
import { MoreHoriz as MoreHorizIcon } from '@mui/icons-material';
import { Grid, IconButton, Menu, MenuItem } from '@mui/material';
import PermissionsComponent from '../../../../components/PermissionsComponent.js';
import { useBenefitTile } from './useBenefitTile.js';

const styles = {
    container: {
        flex: 1,
        border: "2px solid #F1F1F1",
        borderRadius: "5px",
        backgroundColor: '#FFF',
        paddingBottom: "12px"
    },
    subContainer: {
        justifyContent: 'space-between',
        paddingLeft: "20px",
        paddingRight: "20px",
        paddingTop: "10px",
        alignItems: 'center'
    },
    titleContainer: {
        justifyContent: 'space-between',
        paddingLeft: "20px",
        paddingRight: "20px",
        paddingTop: "12px",
        alignItems: 'center'
    }
};

/**
 * Renders a small key-value row for the benefit card.
 * @param {Object} props
 * @param {string} props.label - The description text (e.g., "Effective Date").
 * @param {string|number} props.value - The value to display.
 * @param {string} [props.color] - Optional color override for the value text.
 * @returns {JSX.Element}
 */
const DataRow = ({ label, value, color }) => (
    <Grid container sx={styles.subContainer}>
        <Grid item><Text tiny>{label}</Text></Grid>
        <Grid item><Text tiny bold color={color}>{value}</Text></Grid>
    </Grid>
);

/**
 * A presentational component that renders a benefit information tile.
 * Uses the `useBenefitTile` hook to derive display logic and action availability.
 * @param {Object} props
 * @param {Object} props.benefitData - Raw benefit data from the API/Redux.
 * @param {number} props.benefitData.isPremiumWaived - Flag (0 or 1) indicating waiver status.
 * @param {string} props.benefitData.premiumWaivedEffectiveDate - ISO date string for premium waiver.
 * @param {string} props.benefitData.eligibilityPeriodEnds - ISO date string for coverage start.
 * @param {string} props.benefitData.carrier - The insurance provider name.
 * @param {string} props.benefitName - The key identifying the benefit (e.g., 'dental', 'vision').
 * @param {Object} props.userMeta - Metadata regarding the user's specific plan setup.
 * @param {boolean} props.userMeta.isAsoDental - Whether the user has ASO Dental.
 * @param {boolean} props.userMeta.isAsoHealth - Whether the user has ASO Health.
 * @param {Function} props.openActionModal - Callback to trigger parent modal state.
 * @returns {JSX.Element}
 */
const BenefitTile = ({ benefitData, benefitName, userMeta, openActionModal }) => {
    const [ anchorEl, setAnchorEl ] = useState(null);
    const accessType = useSelector((state) => state.master.access);
    const asoType = useSelector((state) => state.accounts.currentAccount.meta.asoType);

    const {
        title,
        status,
        statusColor,
        hasCoverage,
        showMenu,
        canToggle,
        canWaive,
        carrier,
        displayDates: { effective, waived, termination },
        actions: { toggle, waiver }
    } = useBenefitTile(benefitData, benefitName, userMeta, asoType);

    /**
     * 
     * @param {*} actionType 
     */
    const handleOpenModal = (actionType) => {
        openActionModal(actionType, benefitName);
        setAnchorEl(null);
    };

    return (
        <Grid container sx={styles.container}>
            <Grid container sx={styles.titleContainer}>
                <Grid item>
                    <Text bold medium color={Theme.colors.skyBlue}>{title}</Text>
                </Grid>

                {
                    showMenu && (
                        <PermissionsComponent permission="Toggle Benefits" accessType={accessType} level="read/write">
                            <Grid item>
                                <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} aria-label="options" sx={{ padding: "3px" }}>
                                    <MoreHorizIcon />
                                </IconButton>
                                <Menu anchorOrigin={{ vertical: "top", horizontal: "left" }} anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                                    {
                                        canToggle && (
                                            <MenuItem onClick={() => handleOpenModal(toggle.type)}>
                                                <Text color="inherit" medium>{toggle.label}</Text>
                                            </MenuItem>
                                        )
                                    }
                                    {
                                        canWaive && (
                                            <MenuItem onClick={() => handleOpenModal(waiver.type)}>
                                                <Text color="inherit" medium>{waiver.label}</Text>
                                            </MenuItem>
                                        )
                                    }
                                </Menu>
                            </Grid>
                        </PermissionsComponent>
                    )
                }
            </Grid>

            {hasCoverage && (
                <>
                    <DataRow label="Effective Date" value={effective} />
                    <DataRow label="Carrier" value={carrier} />
                </>
            )}

            <DataRow label="Status" value={status} color={statusColor} />

            {waived && <DataRow label="Premium Waived" value={waived} />}
            {termination && <DataRow label="Termination Date" value={termination} />}
        </Grid>
    );
};

export default BenefitTile;